import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MessageCircle, Heart, Calendar, Gauge, Fuel, Settings, Star, Eye, ChevronLeft, ChevronRight, Car } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAppData } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { secureGetItem, secureSetItem } from '@/lib/storage';
import { generateVehicleSlug } from '@/lib/slug';
import { useStore } from '@/contexts/StoreContext';
import { generateResponsiveImageUrls, getImageLoadingStrategy } from '@/lib/imageOptimization';

interface Vehicle {
  id: number;
  brand: string;
  model: string;
  version: string;
  price: number;
  photos: string[];
  mileage: number;
  fuel: string;
  manufactureYear: number;
  modelYear: number;
  color: string;
  transmission: string;
  isZeroKm: boolean;
  store?: string;
  type?: number;
  statistics?: {
    views?: number;
    favorites?: number;
  };
  optionals?: string[];
}

interface VehicleCardProps {
  vehicle: Vehicle;
  whatsappUrl?: string; // Made optional since we'll use vehicle's store
}

export const VehicleCard = ({ vehicle, whatsappUrl }: VehicleCardProps) => {
  const { config } = useAppData();
  const { colors } = useTheme();
  const { availableStores, selectedStore } = useStore();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorited, setIsFavorited] = useState(false);
  // Use a fallback slug immediately to avoid placeholder flash
  const [vehicleSlug, setVehicleSlug] = useState<string>(() => {
    // Generate a temporary slug immediately to avoid placeholder
    const brand = (vehicle.brand || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-');
    const model = (vehicle.model || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-');
    return `carro/${brand}-${model}-${vehicle.modelYear || vehicle.id}-${vehicle.id}`;
  });
  // Initialize image states - start with true to show content immediately
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(true); // Start as true to render immediately

  // Ensure colors are available immediately
  const buttonColor = colors?.button || '#3b82f6';
  const primaryColor = colors?.primary || '#3b82f6';
  const secondaryColor = colors?.secondary || '#64748b';
  const titleColor = colors?.title || '#1e293b';
  const textColor = colors?.text || '#64748b';

  // Get store info for this vehicle (based on vehicle.store field) - use memo to avoid recalculation
  const storeId = vehicle.store;
  const vehicleStore = useMemo(() => {
    if (!config?.stores) return null;
    return storeId
      ? config.stores.find((s: any) => s._id === storeId) 
      : (selectedStore || config.stores[0]);
  }, [config?.stores, storeId, selectedStore]);
  const showStoreBadge = availableStores.length > 1 && vehicleStore && storeId;

  // Check if vehicle is favorited on mount and generate proper slug - non-blocking
  useEffect(() => {
    // Run async operations without blocking render
    const init = async () => {
      try {
        // Check favorites - non-blocking
        secureGetItem('favorites').then(favorites => {
          if (favorites && Array.isArray(favorites)) {
            setIsFavorited(favorites.includes(vehicle.id));
          }
        }).catch(() => {}); // Silently fail
        
        // Generate proper slug - non-blocking
        generateVehicleSlug(vehicle).then(slug => {
          setVehicleSlug(slug);
        }).catch(() => {}); // Silently fail - keep temporary slug
      } catch (error) {
        // Silently fail - card will still render with temporary slug
      }
    };
    init();
  }, [vehicle.id]);

  // Handle favorite toggle
  const toggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent navigation when clicking on favorite button
    e.stopPropagation();

    const favorites = await secureGetItem('favorites') || [];
    let updatedFavorites = Array.isArray(favorites) ? [...favorites] : [];

    if (updatedFavorites.includes(vehicle.id)) {
      // Remove from favorites
      updatedFavorites = updatedFavorites.filter((id: number) => id !== vehicle.id);
      setIsFavorited(false);
    } else {
      // Add to favorites
      updatedFavorites.push(vehicle.id);
      setIsFavorited(true);
    }

    await secureSetItem('favorites', updatedFavorites);

    // Dispatch custom event to update other components
    window.dispatchEvent(new Event('favoritesUpdated'));
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const formatMileage = (mileage: number) => {
    if (mileage === 0) return 'Zero KM';
    return new Intl.NumberFormat('pt-BR').format(mileage) + ' km';
  };

  const nextImage = () => {
    if (isImageLoading) return; // Block if loading
    setIsImageLoading(true);
    setCurrentImageIndex((prev) =>
      prev === vehicle.photos.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    if (isImageLoading) return; // Block if loading
    setIsImageLoading(true);
    setCurrentImageIndex((prev) =>
      prev === 0 ? vehicle.photos.length - 1 : prev - 1
    );
  };

  // Handle image load complete
  const handleImageLoad = () => {
    setIsImageLoading(false);
    setImageLoaded(true);
  };

  // Preload first image immediately - run synchronously without blocking
  useEffect(() => {
    if (vehicle.photos && vehicle.photos.length > 0 && vehicle.photos[0]) {
      const firstImageUrl = vehicle.photos[0];
      
      // Force immediate load - create image object synchronously
      const img = new Image();
      img.src = firstImageUrl;
      
      // Also add preload link for browser optimization
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = firstImageUrl;
      link.setAttribute('fetchpriority', 'high');
      document.head.appendChild(link);
      
      // Clean up link after image loads or after timeout
      const cleanup = () => {
        setTimeout(() => {
          if (link.parentNode) {
            link.remove();
          }
        }, 2000);
      };
      
      if (img.complete) {
        cleanup();
      } else {
        img.onload = cleanup;
        img.onerror = cleanup;
      }
    }
  }, [vehicle.photos]);

  // Set loading state when image index changes
  useEffect(() => {
    if (vehicle.photos && vehicle.photos[currentImageIndex] && currentImageIndex !== 0) {
      setIsImageLoading(true);
      setImageLoaded(false);
    }
  }, [currentImageIndex, vehicle.photos]);

  const handleWhatsApp = () => {
    const message = `Olá! Tenho interesse no veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version}\nAno: ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nGostaria de mais informações.`;
    // Use vehicle's store whatsapp URL, fallback to prop if provided
    const storeWhatsappUrl = vehicleStore?.whatsapp?.ios || whatsappUrl;
    if (storeWhatsappUrl) {
      const url = `${storeWhatsappUrl}&text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
    }
  };

  const handleImageNavigation = (e: React.MouseEvent, direction: 'prev' | 'next') => {
    e.preventDefault();
    e.stopPropagation();
    if (direction === 'prev') {
      prevImage();
    } else {
      nextImage();
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Block navigation if image is loading
    if (isImageLoading) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <Link 
      to={`/estoque/${vehicleSlug}`} 
      className="block h-full" 
      onClick={handleCardClick} 
      style={{ 
        visibility: 'visible', 
        opacity: 1,
        // Force immediate rendering
        contentVisibility: 'visible',
        containIntrinsicSize: 'auto'
      }}
    >
      <Card 
        className="group overflow-hidden shadow-card hover:shadow-elegant transition-all duration-300 transform flex flex-col h-full cursor-pointer"
        style={{
          // Apply all styles immediately
          backgroundColor: 'hsl(var(--background))',
          // Force immediate paint
          willChange: 'transform, box-shadow'
        }}
      >
        {/* Image Gallery */}
        <div className="relative h-48 overflow-hidden flex-shrink-0 bg-muted">
          {/* Loading Spinner - only show when actively loading */}
          {isImageLoading && !imageLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-muted/50 z-20">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
            </div>
          )}

          {vehicle.photos && vehicle.photos.length > 0 && vehicle.photos[currentImageIndex] ? (
            <>
              {/* Optimized responsive image */}
              {(() => {
                const imageUrl = vehicle.photos[currentImageIndex];
                // Card width is approximately 260px, aspect ratio 4:2.5
                const optimized = generateResponsiveImageUrls(imageUrl, 260, 162);
                const loadingStrategy = getImageLoadingStrategy(currentImageIndex === 0, currentImageIndex === 0);
                
                return (
                  <img
                    src={optimized.src}
                    srcSet={optimized.srcSet}
                    sizes={optimized.sizes || '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 260px'}
                    alt={`${vehicle.brand} ${vehicle.model}`}
                    className="w-full h-full object-cover transition-all duration-300"
                    loading={loadingStrategy.loading}
                    fetchpriority={loadingStrategy.fetchpriority}
                    decoding="async"
                    width={optimized.width}
                    height={optimized.height}
                    style={{ 
                      opacity: 1,
                      visibility: 'visible',
                      display: 'block',
                      contentVisibility: 'visible'
                    }}
                    onLoad={handleImageLoad}
                    onError={(e) => {
                      // Silently handle error - show placeholder instead of broken image
                      e.currentTarget.style.display = 'none';
                      setIsImageLoading(false);
                      setImageLoaded(true);
                    }}
                  />
                );
              })()}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground">
              <Car className="h-12 w-12 opacity-50" />
            </div>
          )}

          {/* Navigation Arrows */}
          {vehicle.photos && vehicle.photos.length > 1 && (
            <>
              <button
                onClick={(e) => handleImageNavigation(e, 'prev')}
                disabled={isImageLoading}
                className={`absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/70 text-white p-2 rounded-full transition-all z-10 hover:bg-black/90 ${isImageLoading ? 'cursor-not-allowed opacity-30' : 'opacity-0 group-hover:opacity-100'}`}
                aria-label={`Ver foto anterior de ${vehicle.brand} ${vehicle.model}`}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => handleImageNavigation(e, 'next')}
                disabled={isImageLoading}
                className={`absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/70 text-white p-2 rounded-full transition-all z-10 hover:bg-black/90 ${isImageLoading ? 'cursor-not-allowed opacity-30' : 'opacity-0 group-hover:opacity-100'}`}
                aria-label={`Ver próxima foto de ${vehicle.brand} ${vehicle.model}`}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </>
          )}

          {/* Image Counter */}
          <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-sm">
            {currentImageIndex + 1} / {vehicle.photos.length}
          </div>

          {/* Badges */}
          <div className="absolute top-2 left-2 flex flex-col gap-1 max-w-[calc(100%-3rem)]">
            {showStoreBadge && (
              <Badge variant="secondary" className="bg-blue-600 p-1 px-2 text-white text-xs flex mr-12 truncate" style={{ background: secondaryColor }}>
                <span className="truncate">{vehicleStore.company}</span>
              </Badge>
            )}
          </div>

          {/* Favorite Button */}
          <button
            onClick={toggleFavorite}
            className="absolute top-2 right-2 p-1.5 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors z-10"
            title={isFavorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
          >
            <Heart
              className={`h-4 w-4 transition-all ${isFavorited ? 'fill-red-500 text-red-500 scale-110' : 'text-gray-600'}`}
            />
          </button>
        </div>

        <CardContent className="p-4 pb-0 flex-grow">
          {/* Vehicle Title */}
          <div className="mb-2">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-foreground mb-1" style={{ color: titleColor }}>
                {vehicle.brand} {vehicle.model}
              </h3>

              {/* Zero KM Badge */}
              {vehicle.isZeroKm && (
                <Badge
                  variant="default"
                  className="bg-green-600 ml-4 text-white text-xs text-center flex items-center whitespace-nowrap"
                >
                  Zero KM
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground text-sm" style={{ color: textColor }}>
              {vehicle.version}
            </p>
          </div>

          {/* Price */}
          <div className="mb-4">
            <div className="text-2xl font-bold text-primary">
              {formatPrice(vehicle.price)}
            </div>
          </div>

          {/* Vehicle Details */}
          <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
            {/* Ano - Always show if exists */}
            {(vehicle.manufactureYear || vehicle.modelYear) && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>{vehicle.manufactureYear}/{vehicle.modelYear}</span>
              </div>
            )}

            {/* KM - Always show */}
            {vehicle.mileage !== undefined && vehicle.mileage !== null && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Gauge className="h-4 w-4" />
                <span>{formatMileage(vehicle.mileage)}</span>
              </div>
            )}

            {/* Fuel - Only if exists */}
            {vehicle.fuel && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Fuel className="h-4 w-4" />
                <span>{vehicle.fuel}</span>
              </div>
            )}

            {/* Transmission - Only for Carro and Caminhão, and if exists */}
            {vehicle.type !== 2 && vehicle.transmission && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Settings className="h-4 w-4" />
                <span>{vehicle.transmission}</span>
              </div>
            )}
          </div>

          {/* Statistics */}
          {/* {vehicle.statistics && (
            <div className="flex items-center gap-4 mb-4 text-sm text-muted-foreground">
              {vehicle.statistics.views && (
                <div className="flex items-center gap-1">
                  <Eye className="h-4 w-4" />
                  <span>{vehicle.statistics.views}</span>
                </div>
              )}
              {vehicle.statistics.favorites && (
                <div className="flex items-center gap-1">
                  <Heart className="h-4 w-4" />
                  <span>{vehicle.statistics.favorites}</span>
                </div>
              )}
            </div>
          )} */}

          {/* Features Preview */}
          {vehicle.optionals && vehicle.optionals.length > 0 && (
            <div className="mb-6">
              <div className="flex flex-wrap gap-1">
                {vehicle.optionals.slice(0, 3).map((optional, index) => (
                  <Badge key={index} variant="outline" className="text-xs">
                    {optional}
                  </Badge>
                ))}
                {vehicle.optionals.length > 3 && (
                  <Badge variant="outline" className="text-xs">
                    +{vehicle.optionals.length - 3} itens
                  </Badge>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </Link >
  );
};